import * as UI from "@iyk/ui"
import * as React from "react"

import { FormState, useForm } from "@iyk/vf-forms"
import { ResendOtpCodeButton } from "@iyk/vf-ui"
import { useLang } from "../../../react-router/use-lang.ts"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../auth/input-otp.tsx"
import { Terms } from "../../ui/terms.tsx"
import { LoginStep } from "../profile.ts"
import { FormButton } from "./form-submit-button.tsx"
import { Routes } from "./link.tsx"
import { Sheet } from "./sheet.tsx"

// #region LoginSheet
export const LoginSheet = ({ returnTo, loginStep, setLoginStep, open, setOpen }: LoginSheet.Props) => {
  return (
    <Sheet.Root open={open} onOpenChange={setOpen}>
      {LoginStep.$match(loginStep, {
        Login: () => (
          <Sheet.Content title="Login" description="Log in or sign up to your account">
            <div className="px-4 pt-12 pb-6">
              <LoginForm
                returnTo={returnTo}
                onEmailLogin={(email) => setLoginStep(LoginStep.VerifyOtp({ email }))}
              />
            </div>
          </Sheet.Content>
        ),
        VerifyOtp: ({ email }) => (
          <Sheet.Content
            title="Check your email"
            description={`Please enter the temporary code we've sent to ${email}`}
            showCloseButton={false}
            // If the user closes the login sheet on the VerifyOtp step,
            // reset the login step to the login step
            onCloseAutoFocus={(e) => {
              e.preventDefault()
              setLoginStep(LoginStep.Login())
            }}
          >
            <div className="px-4 pt-12 pb-6">
              <OtpCodeForm email={email} onBack={() => setLoginStep(LoginStep.Login())} />
            </div>
          </Sheet.Content>
        )
      })}
    </Sheet.Root>
  )
}

namespace LoginSheet {
  export type Props = {
    returnTo: string
    loginStep: LoginStep
    setLoginStep: (loginStep: LoginStep) => void
    open: boolean
    setOpen: (open: boolean) => void
  }
}
// #endregion

// #region LoginForm
const LoginForm = ({
  returnTo,
  onEmailLogin
}: {
  returnTo: string
  onEmailLogin: (email: string) => void
}) => {
  const { Form, errors, state } = useForm()

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const formData = new FormData(e.currentTarget)
    const email = formData.get("email")?.toString()
    if (email) {
      onEmailLogin(email)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base-title mb-3">Log in or Sign up</h3>
      <Form action={Routes.authenticate} method="post" className="flex flex-col gap-3">
        <input type="hidden" name="returnTo" value={returnTo} />
        <input type="hidden" name="_tag" value="OAuth" />

        <UI.Button type="submit" name="provider" value="Google" kind="outline" size="md" className="w-full">
          <UI.Icon.Google />
          Continue with Google
        </UI.Button>
      </Form>
      <div className="flex items-center gap-2">
        <hr className="flex-1 text-gray-4" />
        <span className="text-sm text-gray-9">or</span>
        <hr className="flex-1 text-gray-4" />
      </div>
      <Form
        method="post"
        action={Routes.authenticate}
        className="flex flex-col gap-3"
        onSubmit={handleOnSubmit}
        navigate={false}
      >
        <input type="hidden" name="returnTo" value={returnTo} />
        <input type="hidden" name="_tag" value="Otp" />

        <UI.Field name="email" required validationMessage={errors?.email}>
          <UI.Textbox type="email" placeholder="Email" autoComplete="email" />
          <UI.Validity />
        </UI.Field>

        <FormButton state={state}>Continue with email</FormButton>
      </Form>
      <Terms />
    </div>
  )
}
// #endregion

// #region OtpCodeForm
const OtpCodeForm = ({ email, onBack }: { email: string; onBack: () => void }) => {
  const { Form, errors, submit, state } = useForm()
  const lang = useLang()

  const [otp, setOtp] = React.useState("")

  const handleResendOtpCode = () => {
    submit(null, {
      method: "POST",
      action: Routes.resendOtp,
      preventScrollReset: true
    })

    setOtp("")
  }

  const isFormActive = FormState.$is("Submitting")(state)

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h3 className="text-base-title">Check your email</h3>
        <span className="text-sm text-gray-11 text-pretty">
          Please enter the verification code we&apos;ve sent to {email}. Don&apos;t see it? Be sure to check
          your spam or junk folder.
        </span>
      </div>
      <Form
        method="post"
        action={Routes.verifyOtp}
        className="flex flex-col gap-3"
        navigate={false}
        preventScrollReset={true}
      >
        <div className="flex flex-col gap-2 w-full text-center">
          <InputOTP
            name="otp"
            data-1p-ignore
            maxLength={6}
            value={otp}
            onChange={setOtp}
            onComplete={() => {
              submit(
                { otp },
                {
                  method: "POST",
                  action: Routes.verifyOtp
                }
              )
            }}
          >
            {({ slots }) => (
              <InputOTPGroup>
                {slots.map((slot, index) => (
                  <InputOTPSlot key={index} {...slot} />
                ))}
              </InputOTPGroup>
            )}
          </InputOTP>
          {errors?.otp && <span className="text-sm text-tomato-11">{errors.otp}</span>}
        </div>
        <UI.Button size="md" kind="solid" className="w-full" type="submit" disabled={isFormActive}>
          Continue
        </UI.Button>
        <ResendOtpCodeButton onOtpSent={handleResendOtpCode} lang={lang} />
      </Form>
      <UI.Button type="button" onClick={onBack} kind="ghost" size="sm" className="absolute top-1 left-1">
        <UI.Icon.ArrowLeft />
      </UI.Button>
    </div>
  )
}

// #endregion
